/**
 * Privacy Policy + Terms — real, linkable pages. POPIA requires them, the
 * Google OAuth consent screen wants their URLs, and Safe Browsing reviews go
 * far better when a credential-collecting site states plainly who it is,
 * what it collects and why.
 *
 * Author: Maré Greyling (Thevertex) <mare.greyling@gmail.com> — https://mare-greyling.com
 */
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { api, tokens } from '../api.js';

function Shell({ title, children }) {
  return (
    <div className="min-h-full max-w-3xl mx-auto px-6 py-10">
      <Link to="/" className="flex items-center gap-2 mb-8">
        <img src="/logo.svg" alt="" className="w-8 h-8" />
        <span className="font-bold">Vault</span>
      </Link>
      <h1 className="text-3xl font-bold">{title}</h1>
      <div className="mt-6 space-y-4 text-slate-300 leading-relaxed">{children}</div>
      <p className="mt-10 text-sm text-slate-500">
        Vault is a Maré Greyling product · vault-app.co.za · support@vault-app.co.za ·
        Last updated September 2026
      </p>
    </div>
  );
}

export function Privacy() {
  return (
    <Shell title="Privacy Policy">
      <p>
        Vault (vault-app.co.za) is a personal budgeting service operated by Maré
        Greyling, South Africa. This policy explains what we collect and why, in
        line with the Protection of Personal Information Act (POPIA).
      </p>
      <p>
        <strong className="text-slate-100">What we collect.</strong> Your email
        address and password (stored only as a modern salted hash); the budget
        data you enter or import — banks, amounts, categories, receipt photos and
        bank-statement files you choose to upload; and, if you opt in, a WhatsApp
        number for insights. Payments are handled entirely by Paystack: your card
        is entered on Paystack's secure page and Vault never sees or stores card
        numbers — only a billing reference. Your monthly subscription appears as
        "Mare Greyling" on your card statement.
      </p>
      <p>
        <strong className="text-slate-100">How it's used.</strong> Solely to
        provide the service to you. Some features send your content to third-party
        AI services so it can be read back into your budget.{' '}
        <strong className="text-slate-100">Google Gemini</strong> receives receipt
        and quote images and PDFs, bank-statement transactions, voice recordings you
        dictate, the spend lines you type, and your own expense and category names so
        the result lands on the right expense — together with the coordinates
        attached to a receipt, and then only when you have allowed location.{' '}
        <strong className="text-slate-100">Anthropic Claude</strong> receives the same
        content when Gemini cannot read it, and the description, voice note and
        screenshot you attach to a problem report. Nothing is sent until you have
        allowed it in the app, and you can withdraw that permission at any time under
        Account — every AI feature has a manual alternative. Both providers are bound
        not to keep your content beyond the request and not to train their models on
        it, which is the same protection your data has here. Your data is never sold,
        shared with advertisers, or used to train AI models. Every record is private
        to your account; other users can never see it.
      </p>
      <p>
        <strong className="text-slate-100">Your rights.</strong> Export everything
        you own as a machine-readable file, or delete your account and all its
        data permanently, at any time from inside the app — no email required,
        though support@vault-app.co.za is always available. Data is stored
        encrypted at rest on servers we control.
      </p>
      <p>
        <strong className="text-slate-100">Sign-in providers.</strong> If you use
        Sign in with Google or Apple, we receive only your verified email address
        from the provider — no contacts, no profile data.
      </p>
    </Shell>
  );
}

export function Terms() {
  return (
    <Shell title="Terms of Service">
      <p>
        Vault is a subscription budgeting tool: a 5-day free trial, then R59 per
        month, charged via Paystack to the card you add at signup (statement
        descriptor "Mare Greyling"). Cancel anytime; access continues to the end
        of the paid period. When a trial ends without an active subscription the
        app is gated until you subscribe — your data is kept safe meanwhile.
      </p>
      <p>
        Vault is an information tool, not a financial adviser, bank or payment
        institution. It never moves your money; it records and analyses what you
        tell it or import. Figures — including AI-read receipts, statement
        classifications and suggestions — should be checked before you rely on
        them; you remain responsible for your financial decisions.
      </p>
      <p>
        Fair use applies to AI features (receipt scanning, statement analysis,
        help), which carry monthly per-account quotas. Abuse, attempted access to
        other accounts' data, or use of the service for unlawful purposes ends
        the account. South African law governs these terms.
      </p>
    </Shell>
  );
}


export function DataDeletion() {
  const [step, setStep] = useState('form');      // form -> confirm -> done
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [acct, setAcct] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const signIn = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      const data = await api('/auth/token/', { method: 'POST', body: { username: email.trim(), password } });
      tokens.set(data);
      let who = email.trim();
      try { const me = await api('/auth/me/'); if (me && me.email) who = me.email; } catch { /* non-fatal */ }
      setAcct(who);
      setStep('confirm');
    } catch (err) {
      tokens.clear();
      setError((err && err.data && err.data.detail) || 'That email and password did not match an account.');
    } finally { setBusy(false); }
  };

  const doDelete = async () => {
    setBusy(true); setError('');
    try {
      await api('/auth/delete/', { method: 'POST', body: { password } });
      tokens.clear();
      setStep('done');
    } catch (err) {
      setError((err && err.data && err.data.detail) || 'We could not delete the account. Please try again.');
    } finally { setBusy(false); }
  };

  return (
    <Shell title="Delete your Vault account & data">
      <p>
        You can permanently delete your Vault account and all of its data right
        here. This removes everything you own — banks, budgets, logged items,
        receipt photos and uploaded statements — and cancels any active
        subscription so you are never charged again. It is immediate, permanent
        and cannot be undone.
      </p>

      {step === 'form' && (
        <form onSubmit={signIn} className="card mt-2 space-y-4">
          <p className="text-slate-100 font-semibold">Sign in to delete your account</p>
          <div>
            <label htmlFor="del-email" className="block text-[12px] text-slate-400 mb-1.5">Email or username</label>
            <input id="del-email" className="input" type="text" autoComplete="username" required
                   value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
          </div>
          <div>
            <label htmlFor="del-pw" className="block text-[12px] text-slate-400 mb-1.5">Password</label>
            <input id="del-pw" className="input" type="password" autoComplete="current-password" required
                   value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
          </div>
          {error && <div className="text-[13px] text-red-400 leading-relaxed">{error}</div>}
          <button type="submit" disabled={busy || !email || !password} className="btn-primary w-full disabled:opacity-40">
            {busy ? 'Signing in…' : 'Continue'}
          </button>
          <p className="text-[12.5px] text-slate-500 leading-relaxed">
            Signed up with Google or Apple? Open the Vault app → Account → “Your
            data” → “Delete account” — you’re already signed in there.
          </p>
        </form>
      )}

      {step === 'confirm' && (
        <div className="card mt-2 space-y-4 border-red-500/40">
          <p className="text-slate-100 font-semibold">Permanently delete this account?</p>
          <p className="text-slate-300">
            You are signed in as <strong className="text-slate-100">{acct}</strong>.
            Deleting removes your account and every record it owns, and cancels any
            active subscription. This cannot be undone.
          </p>
          {error && <div className="text-[13px] text-red-400 leading-relaxed">{error}</div>}
          <div className="flex flex-col sm:flex-row gap-3">
            <button type="button" onClick={doDelete} disabled={busy}
                    className="w-full min-h-[48px] rounded-xl border border-red-500/60 bg-red-500/15 text-red-100 font-semibold transition hover:bg-red-500/25 active:scale-[0.99] disabled:opacity-40">
              {busy ? 'Deleting…' : 'Permanently delete my account'}
            </button>
            <button type="button" disabled={busy}
                    onClick={() => { setStep('form'); setPassword(''); setError(''); tokens.clear(); }}
                    className="w-full min-h-[48px] rounded-xl border border-edge text-slate-200 font-medium transition hover:border-slate-500 disabled:opacity-40">
              Cancel
            </button>
          </div>
        </div>
      )}

      {step === 'done' && (
        <div className="card mt-2 border-emerald-500/40">
          <p className="text-slate-100 font-semibold">Your account has been deleted.</p>
          <p className="text-slate-300 mt-2">
            Your Vault account and all of its data have been permanently removed,
            and any active subscription has been cancelled — you will not be
            charged again. Thank you for having used Vault.
          </p>
        </div>
      )}

      <p>
        <strong className="text-slate-100">What is deleted:</strong> your account,
        profile, all budget and transaction data, receipts and statements, and your
        notification preferences.{' '}
        <strong className="text-slate-100">What may be retained:</strong> a minimal
        billing record (a Paystack payment reference and invoice amount — never
        card numbers) where accounting and tax law require it, kept no longer than
        legally required and then deleted.
      </p>
      <p className="text-[13px] text-slate-500">
        Prefer email? Write to{' '}
        <a href="mailto:support@vault-app.co.za" className="text-vpurple underline">support@vault-app.co.za</a>{' '}
        from your account’s email address and we will delete it within 30 days.
      </p>
    </Shell>
  );
}
